/**
 * Shared Arabic keyword detection (BUILD_SPEC.md §9.2, §10.4).
 *
 * Single source of truth for keyword classification across the API,
 * scraper, and SPA. The scraper writes the detected enum onto the
 * Action.containsKeyword column at ingest time; the SPA re-runs the
 * matcher against the original action text to drive inline span
 * wrapping (KeywordHighlight) — both call sites consume this module.
 *
 * Threat / correctness model:
 *   • Real municipality data uses inconsistent Arabic orthography:
 *     ta-marbuta vs ha (موافقة vs موافقه), tashkeel decoration
 *     (مُوَافَقَة), alef variants (إكمال vs اكمال), ya vs alef-maksura
 *     (موسى vs موسي). A naive `String.includes` over raw text misses
 *     real-world variants. We normalize input AND patterns to a
 *     canonical form before matching, so any equivalent spelling
 *     surfaces the right keyword.
 *   • REJECTION must take precedence over APPROVAL because
 *     "غير موافقه" CONTAINS "موافقه" but means the opposite. The
 *     order of category checks below encodes that priority.
 *
 * Imported via the `@shared/keywords` tsconfig path alias from each
 * app's source. Vite (web) and tsx (api / scraper) both resolve the
 * alias through resolve.alias / tsconfig paths respectively.
 */

export type KeywordType = "APPROVAL" | "ASSIGNMENT" | "REJECTION";

/**
 * Order of category traversal — REJECTION first so a string containing
 * "غير موافقه" (which contains the APPROVAL pattern "موافقه") is
 * classified as REJECTION rather than APPROVAL. Do not reorder without
 * updating the §9.2 documentation in BUILD_SPEC.md.
 */
const CATEGORY_ORDER: readonly KeywordType[] = ["REJECTION", "APPROVAL", "ASSIGNMENT"];

/** U+064B–U+0652 = harakat (fatha/damma/kasra/shadda/sukun and friends);
 *  U+0670 = superscript alef. Both are decoration-only and stripped. */
const TASHKEEL_RANGE = /[ً-ْٰ]/g;
const ALEF_VARIANTS = /[إأآا]/g;

/**
 * Normalizes Arabic text for keyword matching (§9.2):
 *   1. strip tashkeel (ً-ْ) and the Arabic letter superscript
 *      alef (ٰ) — pure decoration, never carries semantic weight;
 *   2. fold alef-with-hamza variants [إأآا] to plain alef;
 *   3. fold alef-maksura ى to ya ي (common in non-final positions);
 *   4. fold ta-marbuta ة to ha ه — these are visually different but
 *      typed inconsistently across municipality entries;
 *   5. trim leading/trailing whitespace.
 *
 * The transform is intentionally lossy: "موسى" and "موسي" become the
 * same string. That's fine for a keyword pre-classifier; the original
 * text is preserved upstream for display.
 */
export function normalizeArabic(s: string): string {
  return s
    .replace(TASHKEEL_RANGE, "")
    .replace(ALEF_VARIANTS, "ا")
    .replace(/ى/g, "ي")
    .replace(/ة/g, "ه")
    .trim();
}

/**
 * Patterns written in natural Arabic. They get folded through
 * `normalizeArabic` once at module load time (see `PATTERNS` below) so
 * what we match against is the canonical form — no risk of typing
 * "الى" (alef-maksura) here while inputs normalize to "الي" (ya).
 *
 * Ordering within each list does not matter: PATTERNS reorders
 * longest-first after normalization so a substring scan picks the most
 * specific match (e.g. "تمت الموافقة" wins over a bare "موافقة" at the
 * same start position).
 */
const RAW_PATTERNS: Record<KeywordType, readonly string[]> = {
  REJECTION: ["غير موافقة", "عدم موافقة", "مرفوض", "رفض"],
  APPROVAL: ["تمت الموافقة", "تم اعتماد", "موافقة", "وافق"],
  ASSIGNMENT: ["محولة إلى", "محول إلى", "تكليف", "كلف"],
};

/**
 * Normalized + longest-first patterns. Computed once at import time so
 * every detection or tokenization call is a cheap substring scan over
 * pre-cooked strings. The longest-first sort encodes "match the most
 * specific phrase" without an extra pass at search time.
 */
const PATTERNS: Record<KeywordType, readonly string[]> = {
  REJECTION: RAW_PATTERNS.REJECTION.map(normalizeArabic).sort((a, b) => b.length - a.length),
  APPROVAL: RAW_PATTERNS.APPROVAL.map(normalizeArabic).sort((a, b) => b.length - a.length),
  ASSIGNMENT: RAW_PATTERNS.ASSIGNMENT.map(normalizeArabic).sort((a, b) => b.length - a.length),
};

/**
 * Classifies an action text into one of the three keyword categories.
 *
 * Returns null for empty / null / undefined input so callers can pipe
 * raw API rows through without nullish guards. Categories are checked
 * in `CATEGORY_ORDER` (REJECTION → APPROVAL → ASSIGNMENT); within a
 * category the longest pattern wins because the pattern list is
 * already sorted longest-first.
 */
export function detectKeyword(text: string | null | undefined): KeywordType | null {
  if (!text) return null;
  const norm = normalizeArabic(text);
  if (!norm) return null;
  for (const category of CATEGORY_ORDER) {
    for (const pattern of PATTERNS[category]) {
      if (norm.includes(pattern)) return category;
    }
  }
  return null;
}

/**
 * v1.1 B1 flash-keyword type. Distinct from `KeywordType` on purpose
 * because the v1.1 redesign drops ASSIGNMENT from the card flash and
 * uses different detection semantics. See `detectFlashSpans` and
 * `detectFlashKeyword` (both v1.1.1, 2026-05-14).
 */
export type FlashKeywordType = "APPROVAL" | "REJECTION";

/**
 * Separator character class for flash token splitting. Includes whitespace
 * and punctuation: period, comma, colon, Arabic semicolon, Arabic question
 * mark, exclamation, parentheses, brackets, braces, double/single quote,
 * Arabic comma, forward slash, backslash. Arabic letters (including ة ا ل)
 * are NOT separators, so "الموافقة" stays as one token. Used as the
 * canonical reference for the character class shared by detectFlashSpans.
 */
const FLASH_TOKEN_SEPARATOR = /[\s.,:؛؟!()\[\]{}"'،/\\]+/u;

/**
 * One span emitted by `detectFlashSpans`. The `text` fields of all spans
 * in the returned array concatenate to reproduce the original input exactly.
 * `kind` indicates the flash category for this slice of text:
 *   - "APPROVAL": the standalone token "موافقة".
 *   - "REJECTION": the "عدم" token, the separator(s) between it and the
 *     following "موافقة" token, and that "موافقة" token. All three carry
 *     "REJECTION" so the rendered highlight is visually contiguous.
 *   - null: separator characters, unmatched word tokens, or any other text.
 *
 * v1.1.1 (2026-05-14). See `detectFlashSpans`.
 */
export type FlashSpan = {
  text: string;
  kind: "APPROVAL" | "REJECTION" | null;
};

/**
 * Span-level flash classifier (v1.1.1 exact-token rule, 2026-05-14).
 *
 * Returns an array of `FlashSpan` values whose `text` fields concatenate to
 * reproduce the input exactly. Each span carries a `kind` (see FlashSpan).
 *
 * Detection semantics are identical to `detectFlashKeyword`: split into
 * alternating runs of separator characters and word characters using the
 * character class in `FLASH_TOKEN_SEPARATOR`. Arabic letters are NOT
 * separators, so "الموافقة" stays as one token and returns null.
 *
 *   REJECTION pair scan runs first: adjacent tokens ["عدم", "موافقة"] and
 *   the separator text between them all get kind "REJECTION".
 *
 *   APPROVAL single-token scan runs second: any remaining "موافقة" token
 *   not already tagged REJECTION gets kind "APPROVAL".
 *
 * Returns an empty array for null/undefined/empty/whitespace-only input.
 *
 * `detectFlashKeyword` is implemented in terms of this function so the two
 * always agree: `detectFlashKeyword(text)` equals the highest-precedence
 * kind present in `detectFlashSpans(text)` (REJECTION > APPROVAL > null).
 */
export function detectFlashSpans(text: string | null | undefined): FlashSpan[] {
  if (!text) return [];
  const trimmed = text.trim();
  if (!trimmed) return [];

  // Split the full input (not trimmed) into alternating runs of separator
  // chars and word chars, preserving positions so spans concatenate to input.
  const pieces: Array<{ text: string; isSep: boolean }> = [];
  for (const m of text.matchAll(/[\s.,:؛؟!()\[\]{}"'،/\\]+|[^\s.,:؛؟!()\[\]{}"'،/\\]+/gu)) {
    const t = m[0]!;
    pieces.push({ text: t, isSep: /^[\s.,:؛؟!()\[\]{}"'،/\\]/u.test(t) });
  }

  // Collect word-character (non-separator) pieces with their index in `pieces`.
  const tokenPieces: Array<{ idx: number; text: string }> = [];
  for (let i = 0; i < pieces.length; i++) {
    if (!pieces[i]!.isSep) tokenPieces.push({ idx: i, text: pieces[i]!.text });
  }

  const kindAt = new Map<number, "APPROVAL" | "REJECTION">();

  // REJECTION first: mark the ["عدم", "موافقة"] adjacent-token pair and
  // every separator piece between them as "REJECTION".
  for (let i = 0; i < tokenPieces.length - 1; i++) {
    if (tokenPieces[i]!.text === "عدم" && tokenPieces[i + 1]!.text === "موافقة") {
      const p1 = tokenPieces[i]!.idx;
      const p2 = tokenPieces[i + 1]!.idx;
      kindAt.set(p1, "REJECTION");
      kindAt.set(p2, "REJECTION");
      for (let j = p1 + 1; j < p2; j++) kindAt.set(j, "REJECTION");
    }
  }

  // APPROVAL: any remaining "موافقة" token not already tagged REJECTION.
  for (const tp of tokenPieces) {
    if (!kindAt.has(tp.idx) && tp.text === "موافقة") kindAt.set(tp.idx, "APPROVAL");
  }

  return pieces.map((p, i) => ({ text: p.text, kind: kindAt.get(i) ?? null }));
}

/**
 * Card-flash classifier (v1.1.1 exact-token rule, 2026-05-14).
 *
 * Returns the keyword that should drive the §16.2 whole-card flash, or
 * null. ASSIGNMENT (تكليف) is INTENTIONALLY excluded: in v1.1 it no longer
 * triggers a card flash. The takleef flip in B5 still relies on
 * Action.containsKeyword = "ASSIGNMENT" set by the §9.2 detectKeyword path.
 *
 * Implemented in terms of `detectFlashSpans` (2026-05-14) and guaranteed
 * to agree with it: returns the highest-precedence kind present in the span
 * array (REJECTION > APPROVAL > null). See `detectFlashSpans` for the full
 * detection-semantics documentation.
 */
export function detectFlashKeyword(text: string | null | undefined): FlashKeywordType | null {
  const spans = detectFlashSpans(text);
  if (spans.some((s) => s.kind === "REJECTION")) return "REJECTION";
  if (spans.some((s) => s.kind === "APPROVAL")) return "APPROVAL";
  return null;
}

/**
 * The three event types that drive a flash + distinct sound on the
 * DISPLAY-only Permit Wall (the office TV). Unlike `FlashKeywordType`,
 * this re-admits ASSIGNMENT (تكليف): the v1.1 dashboard dropped it from
 * the card flash, but the wall treats موافقة / عدم موافقة / تكليف as the
 * three equal arrival cues.
 */
export type WallEvent = "APPROVAL" | "REJECTION" | "ASSIGNMENT";

/**
 * Wall arrival-event classifier (DISPLAY-only Permit Wall).
 *
 * Applies the same exact standalone-token rule the dashboard flash uses
 * (a bare leading/standalone "موافقة", the adjacent pair "عدم موافقة", or
 * a bare standalone "تكليف" triggers; an affixed form such as the definite
 * article "الموافقة" / "التكليف" must NOT) and folds all three into a
 * single precedence-ordered result (REJECTION > APPROVAL > ASSIGNMENT).
 *
 * APPROVAL/REJECTION are delegated to `detectFlashSpans` (via
 * `detectFlashKeyword`) so the wall and the dashboard can never disagree
 * on those two. ASSIGNMENT is detected here because the dashboard path
 * deliberately omits it: split on the same separator character class so
 * that "التكليف" stays one token and never equals the bare "تكليف".
 *
 * Returns null for null/undefined/empty/whitespace-only input.
 */
export function detectWallEvent(text: string | null | undefined): WallEvent | null {
  // Reject/approve first, via the canonical span scanner.
  const flash = detectFlashKeyword(text);
  if (flash) return flash;
  if (!text) return null;
  // ASSIGNMENT: a bare standalone "تكليف" token. The separator class
  // matches the one inside `detectFlashSpans`, so affixed forms (the
  // definite article, plurals) collapse into a single token that fails
  // the exact equality check.
  const tokens = text.split(/[\s.,:؛؟!()\[\]{}"'،/\\]+/u);
  return tokens.includes("تكليف") ? "ASSIGNMENT" : null;
}

/**
 * Token emitted by `tokenizeAction`. The `text` field always carries
 * the ORIGINAL input substring (with tashkeel and ta-marbuta intact)
 * so the SPA can render the user's spelling, not the normalized form.
 */
export type Token =
  | { kind: "text"; text: string }
  | { kind: "keyword"; type: KeywordType; text: string };

interface NormalizedMap {
  /** Canonical-form text (output of normalizeArabic without trim). */
  normalized: string;
  /** For each index i in `normalized`, the index in the original text
   *  where that character originated. Allows mapping match positions
   *  back to original-string substrings, preserving tashkeel + variants. */
  originalIndex: number[];
}

/**
 * Like `normalizeArabic` but additionally records, for each character
 * in the normalized output, the index of its origin in the original
 * input. Used by `tokenizeAction` to map normalized-form match positions
 * back to original substrings — the user sees their spelling preserved
 * even when the matcher worked on the canonical form.
 *
 * No `.trim()` is applied here because trimming would shift indices.
 */
function normalizeWithMap(text: string): NormalizedMap {
  const out: string[] = [];
  const idx: number[] = [];
  for (let i = 0; i < text.length; i++) {
    const c = text.charAt(i);
    // Tashkeel is decoration only — it owns no normalized output position.
    // Use `String.match` instead of the global TASHKEEL_RANGE so we don't
    // mutate its lastIndex during iteration.
    if (/[ً-ْٰ]/.test(c)) continue;
    let mapped: string;
    if (c === "إ" || c === "أ" || c === "آ" || c === "ا") mapped = "ا";
    else if (c === "ى") mapped = "ي";
    else if (c === "ة") mapped = "ه";
    else mapped = c;
    out.push(mapped);
    idx.push(i);
  }
  return { normalized: out.join(""), originalIndex: idx };
}

interface RawMatch {
  category: KeywordType;
  /** Inclusive start index in the original text. */
  start: number;
  /** Exclusive end index in the original text. */
  end: number;
}

/**
 * Splits an action string into a sequence of plain-text and keyword-
 * tagged tokens for span-wrapped rendering.
 *
 * Algorithm:
 *   1. Build a normalization map so we can match in canonical form
 *      while still slicing original substrings for display.
 *   2. Scan every (category, pattern) pair against the normalized
 *      string and collect candidate matches.
 *   3. Resolve overlaps: prefer the earliest start; on ties prefer the
 *      longer match; further ties resolved by category order so
 *      REJECTION beats APPROVAL beats ASSIGNMENT.
 *   4. Emit text/keyword tokens in original-string order.
 *
 * Returns an empty array for null/undefined/empty input.
 */
export function tokenizeAction(text: string | null | undefined): Token[] {
  if (!text) return [];
  const { normalized, originalIndex } = normalizeWithMap(text);
  if (!normalized) return [{ kind: "text", text }];

  const candidates: RawMatch[] = [];
  for (const category of CATEGORY_ORDER) {
    for (const pattern of PATTERNS[category]) {
      let from = 0;
      while (from <= normalized.length - pattern.length) {
        const at = normalized.indexOf(pattern, from);
        if (at === -1) break;
        const startInOriginal = originalIndex[at] ?? 0;
        // The exclusive end is the start index of the NEXT normalized
        // char (or the original length if the match runs to the end).
        // This naturally absorbs trailing tashkeel that decorate the
        // last keyword character in the source text.
        const lastNorm = at + pattern.length;
        const endInOriginal =
          lastNorm < originalIndex.length ? (originalIndex[lastNorm] ?? text.length) : text.length;
        candidates.push({ category, start: startInOriginal, end: endInOriginal });
        from = at + pattern.length;
      }
    }
  }

  // Earlier start first; on tie, longer wins; on tie, category order wins.
  const orderRank: Record<KeywordType, number> = { REJECTION: 0, APPROVAL: 1, ASSIGNMENT: 2 };
  candidates.sort((a, b) => {
    if (a.start !== b.start) return a.start - b.start;
    const lenDiff = b.end - b.start - (a.end - a.start);
    if (lenDiff !== 0) return lenDiff;
    return orderRank[a.category] - orderRank[b.category];
  });

  const picked: RawMatch[] = [];
  let cursor = 0;
  for (const m of candidates) {
    if (m.start >= cursor) {
      picked.push(m);
      cursor = m.end;
    }
  }

  const tokens: Token[] = [];
  let pos = 0;
  for (const m of picked) {
    if (m.start > pos) tokens.push({ kind: "text", text: text.slice(pos, m.start) });
    tokens.push({ kind: "keyword", type: m.category, text: text.slice(m.start, m.end) });
    pos = m.end;
  }
  if (pos < text.length) tokens.push({ kind: "text", text: text.slice(pos) });
  return tokens;
}
